import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/Button';
import { buildApiUrl } from '../../config/api';
import { authService } from '../../services/authService';
import { toast } from 'react-hot-toast';

type BusinessAccount = {
  id: string;
  name: string;
  stripeAccountId: string;
  stripeAccountType: string;
  status: string;
  franchiseFeeType: string;
  franchiseFeeValue: number;
  vatMode: string;
  adminFeeAmount?: number | null;
  isActive: boolean;
  createdAt: string;
};

const BusinessAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<BusinessAccount[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [form, setForm] = useState({
    name: '',
    stripeAccountId: '',
    stripeAccountType: 'express',
    franchiseFeeType: 'percent',
    franchiseFeeValue: 20,
    vatMode: 'inclusive',
    adminFeeAmount: '' as any,
    ownerEmail: '',
    country: 'GB',
    businessType: 'company',
  });

  const [onboardingUrlById, setOnboardingUrlById] = useState<Record<string, string>>({});

  const token = authService.getToken();

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const res = await fetch(buildApiUrl('/admin/business-accounts'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load');
      setAccounts(json.data || []);
    } catch (e: any) {
      toast.error(e.message || 'Failed to load business accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const createAccount = async () => {
    try {
      const res = await fetch(buildApiUrl('/admin/business-accounts'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, adminFeeAmount: form.adminFeeAmount ? Number(form.adminFeeAmount) : undefined }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Create failed');
      toast.success('Business account created');
      setForm({ name: '', stripeAccountId: '', stripeAccountType: 'express', franchiseFeeType: 'percent', franchiseFeeValue: 20, vatMode: 'inclusive', adminFeeAmount: '' as any, ownerEmail: '', country: 'GB', businessType: 'company' });
      fetchAccounts();
    } catch (e: any) {
      toast.error(e.message || 'Failed to create');
    }
  };

  const approve = async (id: string) => {
    const res = await fetch(buildApiUrl(`/admin/business-accounts/${id}/approve`), { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast.success('Approved'); fetchAccounts(); } else { toast.error('Approve failed'); }
  };
  const suspend = async (id: string) => {
    const res = await fetch(buildApiUrl(`/admin/business-accounts/${id}/suspend`), { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast.success('Suspended'); fetchAccounts(); } else { toast.error('Suspend failed'); }
  };
  const remove = async (id: string) => {
    const res = await fetch(buildApiUrl(`/admin/business-accounts/${id}`), { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast.success('Deleted'); fetchAccounts(); } else { toast.error('Delete failed'); }
  };

  return (
    <AdminLayout title="Business Accounts">
      <div className="space-y-8">
        {/* Step 1: Company Details */}
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="text-lg font-semibold text-gray-900 mb-4">Step 1: Company Details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input className="border rounded-lg px-3 py-2" placeholder="Company name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} />
            <input className="border rounded-lg px-3 py-2" placeholder="Owner email (for onboarding)" value={form.ownerEmail} onChange={e=>setForm({...form, ownerEmail:e.target.value})} />
          </div>
        </div>

        {/* Step 2: Stripe & Fees */}
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="text-lg font-semibold text-gray-900 mb-4">Step 2: Stripe & Fees</div>
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-3 items-center">
            <input className="border rounded-lg px-3 py-2 lg:col-span-2" placeholder="Stripe Account ID (optional)" value={form.stripeAccountId} onChange={e=>setForm({...form, stripeAccountId:e.target.value})} />
            <select className="border rounded-lg px-3 py-2" value={form.franchiseFeeType} onChange={e=>setForm({...form, franchiseFeeType:e.target.value})}>
              <option value="percent">Percent</option>
              <option value="fixed">Fixed</option>
            </select>
            <input type="number" className="border rounded-lg px-3 py-2" placeholder="Fee value" value={form.franchiseFeeValue} onChange={e=>setForm({...form, franchiseFeeValue:Number(e.target.value)})} />
            <select className="border rounded-lg px-3 py-2" value={form.vatMode} onChange={e=>setForm({...form, vatMode:e.target.value})}>
              <option value="inclusive">VAT Inclusive</option>
              <option value="exclusive">VAT Exclusive</option>
            </select>
            <input type="number" className="border rounded-lg px-3 py-2" placeholder="Admin fee (optional)" value={form.adminFeeAmount} onChange={e=>setForm({...form, adminFeeAmount:e.target.value})} />
          </div>
          <div className="mt-4"><Button onClick={createAccount}>Create Business Account</Button></div>
        </div>

        {/* List */}
        {loading ? (
          <div className="py-16 text-center text-gray-500">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {accounts.map(a => (
              <div key={a.id} className="border rounded-xl p-5 bg-white shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{a.name}</div>
                    <div className="text-xs text-gray-500">{a.stripeAccountId}</div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${a.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{a.status}</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-gray-700">
                  <div><span className="font-medium">Fee:</span> {a.franchiseFeeType} {a.franchiseFeeValue}</div>
                  <div><span className="font-medium">VAT:</span> {a.vatMode}</div>
                  <div><span className="font-medium">Type:</span> {a.stripeAccountType}</div>
                  <div><span className="font-medium">Active:</span> {a.isActive ? 'Yes' : 'No'}</div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Button variant="outline" onClick={()=>approve(a.id)}>Approve</Button>
                  <Button variant="outline" onClick={()=>suspend(a.id)}>Suspend</Button>
                  <Button variant="outline" onClick={()=>remove(a.id)}>Delete</Button>
                  <Button variant="outline" onClick={async()=>{
                    try {
                      const res = await fetch(buildApiUrl(`/admin/business-accounts/${a.id}/stripe/onboarding-link`), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ email: form.ownerEmail })
                      });
                      const json = await res.json();
                      if (!res.ok) throw new Error(json.message || 'Failed');
                      setOnboardingUrlById(prev=>({ ...prev, [a.id]: json.data.url }));
                      toast.success('Onboarding link generated');
                    } catch (e: any) { toast.error(e.message || 'Failed to generate link'); }
                  }}>Onboarding Link</Button>
                </div>
                {onboardingUrlById[a.id] && (
                  <div className="mt-3 text-xs break-all bg-gray-50 border rounded p-2">{onboardingUrlById[a.id]}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BusinessAccounts;


